import React, { useState, useEffect } from 'react';
import { TemplateSection } from './types';
import { fetchTemplateSections, saveTemplateSection, reorderTemplateSections } from './api';
import './ContractTemplateEditor.css';

const PLACEHOLDERS = [
  { key: '{horse_name}', label: 'Pferdename' },
  { key: '{owner_name}', label: 'Besitzername' },
  { key: '{owner_street}', label: 'Straße' },
  { key: '{owner_city}', label: 'PLZ / Ort' },
  { key: '{box_size}', label: 'Boxgröße (3m / 3,50m)' },
  { key: '{start_date}', label: 'Mietbeginn (DD.MM.YYYY)' },
  { key: '{end_date}', label: 'Mietende (DD.MM.YYYY)' },
  { key: '{box_rent}', label: 'Boxenmiete (€)' },
  { key: '{rent_surcharge}', label: 'Mietaufschlag (€)' },
  { key: '{contract_date}', label: 'Vertragsdatum (DD.MM.YYYY)' },
  { key: '{rent_text}', label: 'Pensionspreis-Text (automatisch)' },
  { key: '{rent_duration_text}', label: 'Laufzeit-Text (automatisch)' },
];

const ContractTemplateEditor: React.FC = () => {
  const [sections, setSections] = useState<TemplateSection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchTemplateSections(true)
      .then(data => {
        setSections(data);
        setIsLoading(false);
      })
      .catch(err => {
        console.error('Failed to load template:', err);
        alert('Fehler beim Laden der Vertragsvorlage');
        setIsLoading(false);
      });
  }, []);

  const handleSectionChange = (id: number, field: keyof TemplateSection, value: any) => {
    setSections(sections.map(s => s.id === id ? { ...s, [field]: value } : s));
    setHasChanges(true);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const updated = [...sections];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    updated.forEach((s, i) => { s.sort_order = i + 1; s.clause_number = i + 1; });
    setSections(updated);
    setHasChanges(true);
  };

  const handleMoveDown = (index: number) => {
    if (index === sections.length - 1) return;
    const updated = [...sections];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    updated.forEach((s, i) => { s.sort_order = i + 1; s.clause_number = i + 1; });
    setSections(updated);
    setHasChanges(true);
  };

  const handleAddSection = () => {
    const newSection: TemplateSection = {
      id: 0, // will be assigned by backend
      clause_number: sections.length + 1,
      title: 'Neue Klausel',
      content: '',
      active: true,
      sort_order: sections.length + 1,
    };
    setSections([...sections, newSection]);
    setExpandedId(0);
    setHasChanges(true);
  };

  const handleSaveAll = async () => {
    setSaveStatus('saving');
    try {
      // Save each section individually
      const savedSections: TemplateSection[] = [];
      for (const section of sections) {
        const result = await saveTemplateSection({
          id: section.id || undefined,
          clause_number: section.clause_number,
          title: section.title,
          content: section.content,
          active: section.active,
          sort_order: section.sort_order,
        });
        savedSections.push({ ...section, id: result.section_id });
      }

      // Reorder
      await reorderTemplateSections(
        savedSections.map(s => ({ id: s.id, sort_order: s.sort_order }))
      );

      setSections(savedSections);
      setSaveStatus('saved');
      setHasChanges(false);
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error: any) {
      console.error('Error saving template:', error);
      setSaveStatus('error');
      alert(`Fehler beim Speichern: ${error.message}`);
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  if (isLoading) {
    return (
      <div className="template-editor">
        <div style={{ padding: '2rem', textAlign: 'center' }}>Lade Vertragsvorlage...</div>
      </div>
    );
  }

  return (
    <div className="template-editor">
      <header className="header">
        <h2>Vertragsvorlage bearbeiten</h2>
        <h4>{sections.length} Klauseln | {sections.filter(s => s.active).length} aktiv</h4>
      </header>

      <div className="template-toolbar">
        <div className="placeholder-help">
          <strong>Verfügbare Platzhalter:</strong>
          <div className="placeholder-list">
            {PLACEHOLDERS.map(p => (
              <span key={p.key} className="placeholder-tag" title={p.label}>
                {p.key}
              </span>
            ))}
          </div>
        </div>
        <div className="toolbar-actions">
          {saveStatus !== 'idle' && (
            <span className={`save-indicator save-${saveStatus}`}>
              {saveStatus === 'saving' && 'Speichert...'}
              {saveStatus === 'saved' && 'Gespeichert!'}
              {saveStatus === 'error' && 'Fehler!'}
            </span>
          )}
          <button onClick={handleAddSection} className="btn-add-section">
            + Neue Klausel
          </button>
          <button
            onClick={handleSaveAll}
            className="btn-save-all"
            disabled={!hasChanges || saveStatus === 'saving'}
          >
            Alle speichern
          </button>
        </div>
      </div>

      <div className="sections-list">
        {sections.map((section, index) => {
          const isExpanded = expandedId === section.id;
          return (
            <div
              key={section.id || `new-${index}`}
              className={`section-card ${!section.active ? 'section-inactive' : ''}`}
            >
              <div
                className="section-header"
                onClick={() => setExpandedId(isExpanded ? null : section.id)}
              >
                <div className="section-number">{index + 1}</div>
                <div className="section-title-display">
                  {section.title}
                  {!section.active && <span className="inactive-badge">Deaktiviert</span>}
                </div>
                <div className="section-actions">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleMoveUp(index); }}
                    disabled={index === 0}
                    className="btn-move"
                    title="Nach oben"
                  >
                    ▲
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleMoveDown(index); }}
                    disabled={index === sections.length - 1}
                    className="btn-move"
                    title="Nach unten"
                  >
                    ▼
                  </button>
                  <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                </div>
              </div>

              {isExpanded && (
                <div className="section-body">
                  <div className="section-field">
                    <label>Klausel-Titel</label>
                    <input
                      type="text"
                      value={section.title}
                      onChange={(e) => handleSectionChange(section.id, 'title', e.target.value)}
                    />
                  </div>
                  <div className="section-field">
                    <label>
                      Inhalt
                      <span className="field-hint">
                        Absätze mit Leerzeile trennen. Zeilen mit "- " werden als Aufzählung dargestellt.
                      </span>
                    </label>
                    <textarea
                      value={section.content}
                      onChange={(e) => handleSectionChange(section.id, 'content', e.target.value)}
                      rows={Math.max(6, section.content.split('\n').length + 2)}
                    />
                  </div>
                  <div className="section-toggle">
                    <label>
                      <input
                        type="checkbox"
                        checked={section.active}
                        onChange={(e) => handleSectionChange(section.id, 'active', e.target.checked)}
                      />
                      Klausel aktiv (wird im Vertrag angezeigt)
                    </label>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ContractTemplateEditor;
